// src/utils/companyFieldMapper.js

/**
 * Maps frontend company object to backend API format
 */
export function mapCompanyToBackend(company) {
    const backendCompany = {
        company_name: company.company_name || company.name || null,
        official_name: company.official_name || null,
        registration_number: company.registration_number || company.regNumber || null,
        tax_number: company.tax_number || null,
        vat_number: company.vat_number || null,
        website: company.website || null,
        phone_number: company.phone_number || company.number || null,
        contact_email: company.contact_email || company.email || null,
        accounts_email: company.accounts_email || null,
        description: company.description || null,
        company_status: company.company_status || company.status || 'active',
        discount: company.discount !== undefined ? company.discount : null,
        tags: company.tags || null,
        payment_term_id: company.payment_term_id !== undefined && company.payment_term_id !== null ? company.payment_term_id : null,
        payment_status: company.payment_status || null,
        role_ids: Array.isArray(company.role_ids) ? company.role_ids : (company.roles?.map(r => r.id) ?? []),
        address: {
            address_1: company.address?.address_1 || company.address?.address1 || null,
            address_2: company.address?.address_2 || company.address?.address2 || null,
            city: company.address?.city || company.address?.town || null,
            state: company.address?.state || company.address?.county || null,
            postcode: company.address?.postcode || null,
            country: company.address?.country || null
        }
    };
    
    // Handle billing_address if provided
    if (company.billing_address) {
        backendCompany.billing_address = {
            address_1: company.billing_address?.address_1 || company.billing_address?.address1 || null,
            address_2: company.billing_address?.address_2 || company.billing_address?.address2 || null,
            city: company.billing_address?.city || company.billing_address?.town || null,
            state: company.billing_address?.state || company.billing_address?.county || null,
            postcode: company.billing_address?.postcode || null,
            country: company.billing_address?.country || null
        };
    }
    
    // Remove null values
    Object.keys(backendCompany).forEach(key => {
        if (backendCompany[key] === null) {
            delete backendCompany[key];
        }
    });
    
    // Clean up address object - but keep it if it has any non-empty values
    if (backendCompany.address) {
        const hasAnyValue = Object.values(backendCompany.address).some(val => val !== null && val !== '');
        if (hasAnyValue) {
            // Only remove empty/null values, but keep the address object if it has any data
            Object.keys(backendCompany.address).forEach(key => {
                if (backendCompany.address[key] === null || backendCompany.address[key] === '') {
                    delete backendCompany.address[key];
                }
            });
        } else {
            // Only delete if completely empty
            delete backendCompany.address;
        }
    }
    
    // Clean up billing_address object - but keep it if it has any non-empty values
    if (backendCompany.billing_address) {
        const hasAnyValue = Object.values(backendCompany.billing_address).some(val => val !== null && val !== '');
        if (hasAnyValue) {
            // Only remove empty/null values, but keep the billing_address object if it has any data
            Object.keys(backendCompany.billing_address).forEach(key => {
                if (backendCompany.billing_address[key] === null || backendCompany.billing_address[key] === '') {
                    delete backendCompany.billing_address[key];
                }
            });
        } else {
            // Only delete if completely empty
            delete backendCompany.billing_address;
        }
    }
    
    return backendCompany;
}

/**
 * Maps backend API company object to frontend format
 */
export function mapCompanyFromBackend(backendCompany) {
    return {
        id: backendCompany.id,
        company_name: backendCompany.company_name || '',
        name: backendCompany.company_name || '',
        official_name: backendCompany.official_name || '',
        registration_number: backendCompany.registration_number || '',
        regNumber: backendCompany.registration_number || '',
        tax_number: backendCompany.tax_number || '',
        vat_number: backendCompany.vat_number || '',
        website: backendCompany.website || '',
        phone_number: backendCompany.phone_number || '',
        number: backendCompany.phone_number || '',
        contact_email: backendCompany.contact_email || '',
        email: backendCompany.contact_email || '',
        accounts_email: backendCompany.accounts_email || '',
        description: backendCompany.description || '',
        company_status: backendCompany.company_status || 'active',
        status: backendCompany.company_status || 'active',
        discount: backendCompany.discount || 0,
        tags: backendCompany.tags || [],
        payment_term_id: backendCompany.payment_term_id || null,
        payment_term: backendCompany.payment_term || null,
        payment_status: backendCompany.payment_status || null,
        logo_path: backendCompany.logo_path || backendCompany.image_path || null,
        logo_url: backendCompany.image_url || backendCompany.logo_url || backendCompany.logo_path || null,
        image_url: backendCompany.image_url || backendCompany.logo_url || backendCompany.logo_path || null,
        image_path: backendCompany.image_path || backendCompany.logo_path || null,
        hashid: backendCompany.hashid || null,
        staff: backendCompany.staff || [],
        roles: backendCompany.roles || [],
        role_ids: (backendCompany.roles || []).map(r => r.id),
        address: backendCompany.address ? {
            address_1: backendCompany.address.address_1 || '',
            address1: backendCompany.address.address_1 || '',
            address_2: backendCompany.address.address_2 || '',
            address2: backendCompany.address.address_2 || '',
            city: backendCompany.address.city || '',
            town: backendCompany.address.city || '',
            state: backendCompany.address.state || '',
            county: backendCompany.address.state || '',
            postcode: backendCompany.address.postcode || '',
            country: backendCompany.address.country || ''
        } : {
            address_1: '', address1: '',
            address_2: '', address2: '',
            city: '', town: '',
            state: '', county: '',
            postcode: '',
            country: ''
        },
        billing_address: backendCompany.billing_address ? {
            address_1: backendCompany.billing_address.address_1 || '',
            address1: backendCompany.billing_address.address_1 || '',
            address_2: backendCompany.billing_address.address_2 || '',
            address2: backendCompany.billing_address.address_2 || '',
            city: backendCompany.billing_address.city || '',
            town: backendCompany.billing_address.city || '',
            state: backendCompany.billing_address.state || '',
            county: backendCompany.billing_address.state || '',
            postcode: backendCompany.billing_address.postcode || '',
            country: backendCompany.billing_address.country || ''
        } : null,
        created_at: backendCompany.created_at,
        updated_at: backendCompany.updated_at
    };
}

